export type ReportItemType =
  | "metric"
  | "chip"
  | "progress"
  | "alert";

export type ReportLayout =
  | "grid2"
  | "grid3"
  | "grid4"
  | "row"
  | "stack";

export type ReportFormat =
  | "int"
  | "percent"
  | "text";

export interface ReportMeta {
  title: string;
  subtitle?: string;
  status?: string;
  generatedAt?: string;
  schemaVersion?: number;
}

export interface ReportItem {
  id: string;
  type: ReportItemType;
  label: string;
  value: number | string | null;
  format?: ReportFormat;
  icon?: string;
  color?: string;
  visible: boolean;

  // future-proof flags
  drilldownKey?: string;
  tooltip?: string;
}

export interface ReportSection {
  id: string;
  title: string;
  subtitle?: string;
  layout: ReportLayout;
  visible: boolean;
  items: ReportItem[];
}

export interface FlowReport {
  meta: ReportMeta;
  sections: ReportSection[];
}

export const SAMPLE_FLOW_REPORT: FlowReport = {
  meta: {
    title: "CCH Axcess → Cygnus MDM",
    subtitle: "Execution Report",
    status: "Active",
    schemaVersion: 1
  },
  sections: [
    {
      id: "summary",
      title: "EXECUTION SUMMARY",
      layout: "grid3",
      visible: true,
      items: [
        {
          id: "total",
          type: "metric",
          label: "Total",
          value: 294,
          format: "int",
          icon: "database",
          color: "blue",
          visible: true
        },
        {
          id: "success",
          type: "metric",
          label: "Successful",
          value: 228,
          format: "int",
          icon: "check",
          color: "green",
          visible: true
        },
        {
          id: "failed",
          type: "metric",
          label: "Failed",
          value: 66,
          format: "int",
          icon: "x",
          color: "red",
          visible: true
        }
      ]
    }
  ]
};


export const toTitle = (key: string) => {
  // "ClientName" -> "Client Name", "client_id" -> "Client Id"
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

export const isObjectLike = (v: any) =>
  v !== null && typeof v === "object";

export const stringifyCell = (v: any) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  if (isObjectLike(v)) {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
};

// Choose a stable row id if possible.
// Tries common ID keys first, else falls back to rowIndex.
export const getStableRowId = (row: Record<string, any>) => {
  const candidateKeys = [
    "id",
    "Id",
    "ID",
    "key",
    "Key",
    "FlowOrchestratorInstanceLogId",
    "ClientId",
    "Client Id",
    "TaskId",
    "DocumentGuid",
    "FlowTransactionId",
  ];

  for (const k of candidateKeys) {
    const v = row?.[k];
    if (v !== null && v !== undefined && String(v).trim() !== "") {
      return String(v);
    }
  }

  // absolute fallback: stable-ish hash from content
  // (good enough for display grids; avoids duplicate "undefined")
  try {
    return JSON.stringify(row).slice(0, 2000);
  } catch {
    return String(Math.random());
  }
};

/**
 * Formats a cell value with N/A for null/undefined
 */
export const formatCellValue = (value: any): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  // Format dates nicely
  if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
    try {
      const date = new Date(value);
      return date.toLocaleString();
    } catch {
      return value;
    }
  }

  return stringifyCell(value);
};

/**
 * Formats a value based on its format type
 */
export const formatValue = (
  value: number | string | null,
  format?: ReportFormat
): string => {
  if (value === null || value === undefined) return "—";
  
  if (format === "int" && typeof value === "number") {
    return value.toLocaleString();
  }
  
  if (format === "percent" && typeof value === "number") {
    return `${value}%`;
  }
  
  return String(value);
};